import React from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../../config/api';
import CompanyNav from '../../components/CompanyNav';
import { useTranslation } from '../../hooks/useTranslation';
import { setLocale } from '../../utils/translations';

const Settings = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { t, locale, setLocale: setLocaleState } = useTranslation();

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditAccount = () => {
    navigate('/company/edit-profile');
  };

  const handleChangePassword = async () => {
    const dir = locale === 'ar' ? 'rtl' : 'ltr';
    const textAlign = locale === 'ar' ? 'right' : 'left';
    const { value: formValues } = await Swal.fire({
      title: `<div style="font-size: 1.5rem; font-weight: 700; color: #111; margin-bottom: 0.5rem;">🔒 ${t('company.settings.changePasswordTitle')}</div>`,
      html: `
          <div class="swal2-form-container" style="text-align: ${textAlign}; direction: ${dir}; max-width: 500px; margin: 0 auto;">
              <div class="form-field-group">
                  <label class="form-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      ${t('company.settings.currentPassword')}
                  </label>
                  <input id="current_password" type="password" class="swal2-input-modern" placeholder="${t('company.settings.currentPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
              </div>
              <div class="form-field-group">
                  <label class="form-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      ${t('company.settings.newPassword')}
                  </label>
                  <input id="new_password" type="password" class="swal2-input-modern" placeholder="${t('company.settings.newPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
                  <small class="form-hint">${t('company.settings.passwordHint')}</small>
              </div>
              <div class="form-field-group">
                  <label class="form-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-${locale === 'ar' ? 'left' : 'right'}: 0.5rem; color: #608fd3;">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      ${t('company.settings.confirmPassword')}
                  </label>
                  <input id="confirm_password" type="password" class="swal2-input-modern" placeholder="${t('company.settings.confirmPasswordPlaceholder')}" style="direction: ${dir}; text-align: ${textAlign};" />
              </div>
          </div>
      `,
      width: '600px',
      padding: '2rem',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> ${t('company.settings.save')}</span>`,
      cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('company.settings.cancel')}</span>`,
      confirmButtonColor: '#608fd3',
      cancelButtonColor: '#6b7280',
      reverseButtons: locale === 'ar',
      customClass: {
        popup: 'swal2-popup-modern',
        title: 'swal2-title-modern',
        htmlContainer: 'swal2-html-container-modern',
        confirmButton: 'swal2-confirm-modern',
        cancelButton: 'swal2-cancel-modern'
      },
      preConfirm: () => {
        const currentPassword = document.getElementById('current_password').value.trim();
        const newPassword = document.getElementById('new_password').value.trim();
        const confirmPassword = document.getElementById('confirm_password').value.trim();
        if (!currentPassword) {
          Swal.showValidationMessage(`⚠️ ${t('company.settings.currentPasswordRequired')}`);
          return false;
        }
        if (!newPassword) {
          Swal.showValidationMessage(`⚠️ ${t('company.settings.newPasswordRequired')}`);
          return false;
        }
        if (newPassword.length < 8) {
          Swal.showValidationMessage(`⚠️ ${t('company.settings.newPasswordMinLength')}`);
          return false;
        }
        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage(`⚠️ ${t('company.settings.confirmPasswordMismatch')}`);
          return false;
        }
        return {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: confirmPassword
        };
      }
    });
    if (formValues) {
      try {
        await api.post('/auth/change-password', formValues);
        toast.success(t('company.settings.passwordChangeSuccess'));
      } catch (error) {
        const message = error.response?.data?.message || t('company.settings.passwordChangeFailed');
        toast.error(message);
      }
    }
  };

  const handleChangeLanguage = () => {
    const newLocale = locale === 'ar' ? 'en' : 'ar';
    setLocale(newLocale);
    setLocaleState(newLocale);
    toast.success(t('company.settings.languageChanged'));
    // Force page reload to apply language change
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleLogout = async () => {
    const dir = locale === 'ar' ? 'rtl' : 'ltr';
    const result = await Swal.fire({
      html: `<div style="text-align: center; direction: ${dir};">
                <p>${t('company.settings.logoutTitle')}</p>
            </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg> ${t('company.settings.logoutButton')}</span>`,
      cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('company.settings.cancel')}</span>`,
      confirmButtonColor: '#608fd3',
      cancelButtonColor: '#6b7280',
      reverseButtons: locale === 'ar',
      customClass: {
        popup: 'swal2-popup-modern',
        confirmButton: 'swal2-confirm-modern',
        cancelButton: 'swal2-cancel-modern'
      }
    });
    if (result.isConfirmed) {
      try {
        try {
          await api.post('/auth/logout');
        } catch (e) {}
        authService.logout();
        toast.success(t('company.settings.logoutSuccess'));
        navigate('/login');
      } catch (error) {
        authService.logout();
        navigate('/login');
      }
    }
  };

  const isEmployeeRole = (user?.user_company_role || user?.company_role || user?.roleInCompany) === 'employee';

  const baseMenu = [
    {
        id: 'change-language',
        title: t('company.settings.changeLanguage'),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
        ),
        onClick: handleChangeLanguage
    },
    {
      id: 'edit-account',
      title: t('company.settings.editCompanyProfile'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
      onClick: handleEditAccount,
      highlighted: true
    },
    {
      id: 'manage-users',
      title: t('company.settings.manageUsers'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M19 8a4 4 0 1 1-7.9 1" />
        </svg>
      ),
      onClick: () => navigate('/company/users')
    },
    {
      id: 'change-password',
      title: t('company.settings.changePassword'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      ),
      onClick: handleChangePassword
    },
    {
      id: 'edit-user',
      title: t('company.settings.editMyAccount'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
      onClick: () => navigate('/company/settings/edit-user')
    },
    {
      id: 'logout',
      title: t('company.settings.logout'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#608fd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          <polyline points="16 17 21 12 16 7"></polyline>
          <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>
      ),
      onClick: handleLogout
    }
  ];

  const menuItems = isEmployeeRole
    ? baseMenu.filter(item => !['edit-account', 'manage-users'].includes(item.id))
    : baseMenu;

  return (
    <div className="employee-settings-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="notification-icon">
          <span className="bell-icon">🔔</span>
        </div>
        <div className="settings-title">{t('company.settings.title')}</div>
        <button className="settings-back-btn" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={locale === 'ar' ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
          </svg>
        </button>
      </div>

      {/* Menu */}
      <div className="settings-menu-container">
        {menuItems.map((item) => (
          <div
            key={item.id}
            className={`settings-menu-item`}
            onClick={item.onClick}
          >
            <div className="settings-menu-text">{item.title}</div>
            <div className="settings-menu-icon">{item.icon}</div>
          </div>
        ))}
      </div>

      <CompanyBottomNavigation />
    </div>
  );
};

export default Settings;


